/**
 * A MARGEM COMPLETA (0096), do lado da tela — o que a mesa e o celular
 * dividem, fora do componente, onde o teste alcança: o formulário da
 * depreciação e do custo indireto, o estorno, e as frases que acompanham o
 * custo total.
 *
 * As contas são do servidor (`backend/src/custos/margem.ts`). As FRASES do que
 * ficou fora também: a tela escreve a lista `ausencias` como veio, e nunca a
 * sua — texto copiado diverge na primeira correção.
 */
import { formatarCompetencia, formatarDia, formatarInteiro, formatarReais, interpretarNumero } from '@/lib/formato';
import { faltaNoMotivo } from '@/lib/motivo';

import type { DepreciacaoDaMaquina, NovaDepreciacao, NovoIndireto, VigenciaDaDepreciacao } from './api';

/* ============================================================ a margem */

/** "Margem" quando nada ficou fora do recorte; "Margem parcial" quando algo ficou. */
export function rotuloDaMargem(completo: boolean | undefined): string {
  return completo ? 'Margem' : 'Margem parcial';
}

/** "Resultado" e "Resultado parcial", pela mesma regra. */
export function rotuloDoResultado(completo: boolean | undefined): string {
  return completo ? 'Resultado' : 'Resultado parcial';
}

/**
 * A frase que acompanha o custo total: completa quando a lista do servidor
 * está vazia; senão, "Parcial:" com cada ausência como veio. Servidor anterior
 * (sem a lista): diz que o total é só o direto.
 */
export function fraseDoCustoTotal(ausencias: readonly string[] | undefined, completo: boolean | undefined): string {
  if (ausencias === undefined) return 'Parcial: custo direto, sem depreciação nem custo indireto rateado.';
  if (completo && ausencias.length === 0) {
    return 'Custo total completo: direto, depreciação e custo indireto rateado. Nada ficou fora.';
  }
  return `Parcial: ${ausencias.join('; ')}.`;
}

/* ======================================================= a depreciação */

export interface ValoresDaDepreciacao {
  /** Vale a partir de (vigência). */
  readonly desde: string;
  /** Início da depreciação. */
  readonly inicio: string;
  readonly aquisicao: string;
  readonly residual: string;
  readonly vida: string;
  readonly motivo: string;
}

const dataValida = (v: string) => /^\d{4}-\d{2}-\d{2}$/.test(v);

/** O que falta, por campo. Nada falta: objeto vazio. */
export function faltaNaDepreciacao(v: ValoresDaDepreciacao): Partial<Record<keyof ValoresDaDepreciacao, string>> {
  const falta: Partial<Record<keyof ValoresDaDepreciacao, string>> = {};
  if (!dataValida(v.desde)) falta.desde = 'Informe a data em que estes dados passam a valer.';
  if (!dataValida(v.inicio)) falta.inicio = 'Informe o dia em que a máquina começou a depreciar, como a data da compra.';
  const aquisicao = interpretarNumero(v.aquisicao);
  if (aquisicao === null || aquisicao <= 0) falta.aquisicao = 'Informe o valor de aquisição em reais, maior que zero, como 1.300.000,00.';
  const residual = interpretarNumero(v.residual);
  if (residual === null || residual < 0) falta.residual = 'Informe o valor residual em reais, ou 0.';
  else if (aquisicao !== null && residual >= aquisicao) falta.residual = 'O valor residual fica abaixo do valor de aquisição.';
  const vida = Number(v.vida.trim());
  if (!/^\d+$/.test(v.vida.trim()) || vida < 1 || vida > 600) falta.vida = 'Informe a vida útil em meses, de 1 a 600, como 120.';
  const motivo = faltaNoMotivo(v.motivo);
  if (motivo) falta.motivo = motivo;
  return falta;
}

export function corpoDaDepreciacao(v: ValoresDaDepreciacao): NovaDepreciacao {
  return {
    desde: v.desde,
    inicio: v.inicio,
    valorAquisicao: interpretarNumero(v.aquisicao) ?? 0,
    valorResidual: interpretarNumero(v.residual) ?? 0,
    vidaUtilMeses: Number(v.vida.trim()),
    motivo: v.motivo.trim(),
  };
}

/** A parcela mensal do que está no formulário, ou nula enquanto faltar valor. */
export function parcelaDoFormulario(v: ValoresDaDepreciacao): number | null {
  const a = interpretarNumero(v.aquisicao);
  const r = interpretarNumero(v.residual);
  const vida = Number(v.vida.trim());
  if (a === null || r === null || !(vida >= 1) || r >= a) return null;
  return Math.round(((a - r) / vida) * 100) / 100;
}

/** "120 meses (10 anos)", "18 meses (1 ano e 6 meses)". */
export function vidaUtilEscrita(meses: number): string {
  const anos = Math.floor(meses / 12);
  const resto = meses % 12;
  const base = `${formatarInteiro(meses)} ${meses === 1 ? 'mês' : 'meses'}`;
  if (anos === 0) return base;
  const emAnos = `${formatarInteiro(anos)} ${anos === 1 ? 'ano' : 'anos'}${resto > 0 ? ` e ${formatarInteiro(resto)} ${resto === 1 ? 'mês' : 'meses'}` : ''}`;
  return `${base} (${emAnos})`;
}

/** "R$ 10.000,00/mês" */
export function parcelaEscrita(parcela: number): string {
  return `${formatarReais(parcela)}/mês`;
}

/** A linha de resumo da depreciação vigente, ou a lacuna escrita. */
export function resumoDaDepreciacao(v: VigenciaDaDepreciacao | null): string {
  if (v === null) return 'Sem dado de depreciação: a depreciação desta máquina fica fora do custo total.';
  return `${parcelaEscrita(v.parcelaMensal)} · aquisição ${formatarReais(v.valorAquisicao)}, residual ${formatarReais(v.valorResidual)}, ${vidaUtilEscrita(v.vidaUtilMeses)} desde ${formatarDia(v.inicio)}`;
}

/**
 * Os valores iniciais do formulário: os da vigência de hoje, quando há (a
 * correção parte do que está lá); senão, em branco com a vigência começando
 * no 1º dia do mês anterior — o mês fechado já sai com depreciação.
 */
export function valoresIniciaisDaDepreciacao(m: DepreciacaoDaMaquina | undefined, hoje: Date): ValoresDaDepreciacao {
  const ano = hoje.getMonth() === 0 ? hoje.getFullYear() - 1 : hoje.getFullYear();
  const mes = hoje.getMonth() === 0 ? 12 : hoje.getMonth();
  const mesAnterior = `${String(ano)}-${String(mes).padStart(2, '0')}-01`;
  const v = m?.vigente ?? null;
  if (v === null) return { desde: mesAnterior, inicio: '', aquisicao: '', residual: '', vida: '', motivo: '' };
  const decimal = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return {
    desde: mesAnterior,
    inicio: v.inicio,
    aquisicao: decimal(v.valorAquisicao),
    residual: decimal(v.valorResidual),
    vida: String(v.vidaUtilMeses),
    motivo: '',
  };
}

/** O efeito no retorno (R04): a parcela antes → depois. */
export function efeitoDaDepreciacao(m: DepreciacaoDaMaquina | undefined, desde: string, novaParcela: number) {
  const anterior = m?.historico.find((h) => h.desde <= desde && (h.ate === null || h.ate > desde)) ?? null;
  return {
    rotulo: `Depreciação desde ${formatarDia(desde)}`,
    antes: anterior ? parcelaEscrita(anterior.parcelaMensal) : 'sem dado',
    depois: parcelaEscrita(novaParcela),
  };
}

/* ===================================================== o custo indireto */

export interface ValoresDoIndireto {
  readonly centroCustoId: string;
  /** `aaaa-mm`, o valor do campo de mês. */
  readonly mes: string;
  readonly valor: string;
  readonly descricao: string;
}

/** O mês anterior ao de hoje, `aaaa-mm`: o mês que se fecha. */
export function mesQueSeFecha(hoje: Date): string {
  const ano = hoje.getMonth() === 0 ? hoje.getFullYear() - 1 : hoje.getFullYear();
  const mes = hoje.getMonth() === 0 ? 12 : hoje.getMonth();
  return `${String(ano)}-${String(mes).padStart(2, '0')}`;
}

export function faltaNoIndireto(v: ValoresDoIndireto): Partial<Record<keyof ValoresDoIndireto, string>> {
  const falta: Partial<Record<keyof ValoresDoIndireto, string>> = {};
  if (v.centroCustoId === '') falta.centroCustoId = 'Escolha o centro de custo.';
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(v.mes)) falta.mes = 'Escolha o mês da competência.';
  const valor = interpretarNumero(v.valor);
  if (valor === null || valor <= 0) falta.valor = 'Informe o valor em reais, maior que zero, como 18.500,00.';
  if (v.descricao.trim().length < 3) falta.descricao = 'Diga do que é o custo, como "aluguel do pátio de setembro".';
  return falta;
}

export function corpoDoIndireto(v: ValoresDoIndireto): NovoIndireto {
  return { centroCustoId: v.centroCustoId, competencia: `${v.mes}-01`, valor: interpretarNumero(v.valor) ?? 0, descricao: v.descricao.trim() };
}

/** O piso do estorno é o do banco (`org.estorno`): 15 caracteres. */
export const MOTIVO_MINIMO_DO_ESTORNO = 15;

export function faltaNoEstorno(motivo: string): string | null {
  const n = motivo.trim().length;
  if (n === 0) return 'Escreva o que estava errado no lançamento.';
  if (n < MOTIVO_MINIMO_DO_ESTORNO) {
    const faltam = MOTIVO_MINIMO_DO_ESTORNO - n;
    return `Escreva mais ${String(faltam)} ${faltam === 1 ? 'caractere' : 'caracteres'} no motivo.`;
  }
  if (n > 300) return 'O motivo passou de 300 caracteres. Encurte.';
  return null;
}

/** "09/2026" */
export function competenciaEscrita(competencia: string): string {
  return formatarCompetencia(competencia);
}

/** A soma do que vale (o estornado não soma). */
export function totalVigente(lancamentos: readonly { readonly valor: number; readonly estornadoEm: string | null }[]): number {
  return Math.round(lancamentos.filter((l) => l.estornadoEm === null).reduce((s, l) => s + l.valor, 0) * 100) / 100;
}
